import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ChevronDownIcon, CopyIcon } from 'lucide-react'
import { TextContent, TextItem } from 'pdfjs-dist/types/src/display/api'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'

import { DocumentQuoteSchema } from '@/gen/artifacts/v1/model_pb'
import { DocumentFact } from '@/gen/inventory/v1/control_service_pb'

import {
  HighlightRangesMap,
  calculateHighlightData,
  getHighlightId,
  getItemKey,
} from '@/lib/assessment/pdfjs-highlight-utils'
import { sortAndMergeRanges } from '@/lib/assessment/quote-matching'
import '@/lib/pdf/worker'
import { cn } from '@/lib/ui/style-helpers'

import { UnknownError } from '@/pages/error-pages/unknown-error/unknown-error'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { ArtifactExpirationDate } from '@/components/shared/artifact-expiration-date/artifact-expiration-date'
import { ErrorBoundary } from '@/components/shared/error-boundary/error-boundary'
import { SourceDocumentStaticViewer } from '@/components/shared/source-document-static-viewer/source-document-static-viewer'

import { DocumentFactFilePreview, PdfLoading } from './document-fact-file-preview'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

type DocumentFactViewerProps = {
  documentFact: DocumentFact
  defaultExpanded?: boolean
  setHasHighlight?: (hasHighlight: boolean) => void
  fallbackLink?: string
  quoteContexts?: string[]
}

export const DocumentFactViewer = React.memo(
  ({
    documentFact,
    defaultExpanded = false,
    setHasHighlight,
    fallbackLink,
    quoteContexts,
  }: DocumentFactViewerProps) => {
    const firstHighlightIdRef = useRef<string | null>(null)
    const highlightRangesMapRef = useRef<HighlightRangesMap>(new Map())
    const [isExtended, setIsExtended] = useState(defaultExpanded)
    const [isHighlightDataReady, setIsHighlightDataReady] = useState(false)
    const [isError, setIsError] = useState(false)
    const instanceId = useId()

    const { toast } = useToast()

    const factKey = `${documentFact.link}|${documentFact.page}|${documentFact.quotes.join('\u0000')}`

    useEffect(() => {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsError(false)
      // oxlint-disable-next-line react/set-state-in-effect
      setIsHighlightDataReady(false)
      highlightRangesMapRef.current = new Map()
      firstHighlightIdRef.current = null
    }, [factKey])

    const { quotes, page, link, name, subcategory, expirationDate } = documentFact
    // page is only set (>= 1) for PDF documents; undefined for non-PDF sources

    const staticViewer = (
      <SourceDocumentStaticViewer
        link={fallbackLink ?? link}
        documentQuote={create(DocumentQuoteSchema, {
          quoteEntries: (quoteContexts ?? []).map((ctx, i) => ({
            text: documentFact.quotes[i] ?? '',
            context: ctx,
          })),
          page: documentFact.page,
          expirationDate: documentFact.expirationDate,
          name: documentFact.name,
          source: documentFact.source,
          uploadedAt: documentFact.uploadedAt,
          subcategory: documentFact.subcategory,
        })}
      />
    )

    const highlightSearch = useCallback(
      (item: TextItem) => {
        const itemKey = getItemKey(item)
        const ranges = highlightRangesMapRef.current.get(itemKey)

        if (!ranges || ranges.length === 0) {
          return item.str
        }

        const mergedRanges = sortAndMergeRanges(ranges)

        let result = ''
        let lastIndex = 0
        const highlightId = getHighlightId(link, page, instanceId)

        mergedRanges.forEach((range, index) => {
          result += item.str.substring(lastIndex, range.start)
          const idAttribute = index === 0 ? ` id="${highlightId}"` : ''
          result += `<span class="bg-marker-highlight" ${idAttribute}>${item.str.substring(range.start, range.end)}</span>`
          lastIndex = range.end
        })

        result += item.str.substring(lastIndex)

        return result
      },
      [link, page, instanceId],
    )

    const customTextRenderer = useCallback(
      (textItem: TextItem) => {
        return highlightSearch(textItem)
      },
      [highlightSearch],
    )

    const handleCopyQuotes = useCallback(() => {
      navigator.clipboard.writeText(quotes.join('\n'))
      toast({
        title: 'Quotes copied to clipboard',
        variant: 'success',
      })
    }, [quotes, toast])

    const scrollToHighlight = useCallback(
      (isExpanded: boolean) => {
        if (firstHighlightIdRef.current) {
          const el = document.getElementById(firstHighlightIdRef.current)
          const parent = document.querySelector(
            `div:has(> .react-pdf__Document #${firstHighlightIdRef.current})`,
          )
          if (el && parent) {
            const parentRect = parent.getBoundingClientRect()
            const elementRect = el.getBoundingClientRect()
            const relativeTop = elementRect.top - parentRect.top + parent.scrollTop

            let scrollPosition = 0
            if (isExpanded) {
              scrollPosition = relativeTop - 200
            } else {
              scrollPosition = relativeTop - 20
            }

            // Scroll to the highlight after the expansion or collapse animation is complete
            setTimeout(() => {
              parent.scrollTo({
                behavior: 'smooth',
                top: Math.max(0, scrollPosition),
              })
            }, 200)
          }
        }
      },
      [firstHighlightIdRef],
    )

    const onRenderTextLayerSuccess = useCallback(() => {
      scrollToHighlight(isExtended)
    }, [scrollToHighlight, isExtended])

    if (quotes.length === 0 && !page) return <DocumentFactFilePreview documentFact={documentFact} />

    if (!page) {
      return staticViewer
    }

    const onGetTextSuccess = (textContent: TextContent) => {
      if (isHighlightDataReady) return

      const { highlightRangesMap: newHighlightRangesMap, firstHighlightId: newFirstHighlightId } =
        calculateHighlightData(textContent, quotes, link, page, quoteContexts, instanceId)
      highlightRangesMapRef.current = newHighlightRangesMap
      firstHighlightIdRef.current = newFirstHighlightId
      setHasHighlight?.(!!newFirstHighlightId)
      setIsHighlightDataReady(true)
    }

    if (isError || !link) {
      return staticViewer
    }

    const viewer = (
      <div className='flex w-full flex-col rounded border border-main-border-default shadow'>
        <div className='flex flex-row justify-between overflow-hidden border-b border-b-main-border-default p-2'>
          <div className='flex min-w-0 flex-1 flex-row gap-2'>
            {subcategory && (
              <Badge variant='secondary' className='text-nowrap rounded'>
                {subcategory}
              </Badge>
            )}
            <Hyperlink className='truncate' href={link}>
              {name}
            </Hyperlink>
            <p className='shrink-0 text-main-foreground-tertiary'>p.{page}</p>
          </div>
          <div className='flex shrink-0 flex-row gap-2'>
            {expirationDate && (
              <ArtifactExpirationDate expirationDate={timestampDate(expirationDate)} />
            )}
            {/* Full-document citations have no verbatim quotes; only show the
                Copy quotes affordance when there is something to copy. */}
            {quotes.length > 0 && (
              <Tooltip
                // eslint-disable-next-line react-hooks/refs
                tooltipDisabled={!!firstHighlightIdRef.current}
                trigger={
                  <Button
                    onClick={handleCopyQuotes}
                    // eslint-disable-next-line react-hooks/refs
                    disabled={!firstHighlightIdRef.current}
                    variant='text'
                    size='sm'
                    className='text-main-foreground-tertiary'
                    asChild
                  >
                    <TextWithIcon
                      text='Copy quotes'
                      icon={<CopyIcon className='size-3.75' strokeWidth={1.5} />}
                    />
                  </Button>
                }
              >
                Quote is not available for this page
              </Tooltip>
            )}
          </div>
        </div>

        <div
          className={cn(
            'overflow-hidden transition-all duration-100',
            isExtended ? 'h-112' : 'h-32',
          )}
        >
          <Document
            className='-ml-8'
            file={link}
            loading={<PdfLoading />}
            error={<UnknownError />}
            onLoadError={() => setIsError(true)}
            onLoadSuccess={() => setIsError(false)}
          >
            <Page
              loading={<PdfLoading />}
              pageNumber={page}
              scale={1.25}
              onGetTextSuccess={onGetTextSuccess}
              customTextRenderer={customTextRenderer}
              onRenderTextLayerSuccess={onRenderTextLayerSuccess}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>
        <div className='z-10 -mt-10 flex h-10 w-full items-center justify-center bg-gradient-to-b from-transparent to-main-background-muted-dark'>
          <Button
            className='rounded border border-main-border-default bg-main-background-card text-main-foreground-tertiary'
            variant='ghost'
            size='sm'
            onClick={() => {
              const newState = !isExtended
              setIsExtended(newState)
              setTimeout(() => scrollToHighlight(newState), 50)
            }}
          >
            <TextWithIcon
              text={isExtended ? 'Collapse' : 'Expand'}
              icon={
                <ChevronDownIcon
                  className={cn(
                    'size-3.75',
                    isExtended && 'rotate-180 transition-all duration-300',
                  )}
                  strokeWidth={1.5}
                />
              }
            />
          </Button>
        </div>
      </div>
    )

    // key: a stale pdf.js transport must never reach the next document (ENG-4311).
    return (
      <ErrorBoundary key={link} fallback={staticViewer}>
        {viewer}
      </ErrorBoundary>
    )
  },
)

DocumentFactViewer.displayName = 'DocumentFactViewer'
